import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Upload, Trash2, Download, Film, Image as ImageIcon, FolderOpen } from "lucide-react";

const Placement = () => {
    const { placementId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [outcome, setOutcome] = useState({ brand_rating: 5, brand_rating_notes: "", outcome_data: { reach: 0, engagement: 0 }, commercial_usage_rights: "Paid social, 90 days." });
    const [showFinalize, setShowFinalize] = useState(false);

    const load = useCallback(() => api.get(`/placement/${placementId}`).then((r) => setData(r.data)), [placementId]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <DashboardShell role={user?.role} title="Placement"><div className="font-heading">Loading…</div></DashboardShell>;
    const { placement, campaign, creator, brand } = data;
    const isBrand = user?.role === "brand" || user?.role === "admin";
    const isCreator = user?.role === "creator" || user?.role === "admin";

    const act = async (i, action, extra = {}) => {
        await api.post(`/placement/${placementId}/deliverable/${i}`, { action, ...extra });
        await load();
    };

    const finalize = async () => {
        await api.post(`/placement/${placementId}/finalize`, outcome);
        await load();
        setShowFinalize(false);
    };

    const requestSwap = async () => {
        if (!window.confirm("Request creator swap? Campaign returns to matching.")) return;
        await api.post(`/placement/${placementId}/request-swap`);
        navigate(user?.role === "brand" ? "/brand/campaigns" : "/admin/matching");
    };

    return (
        <DashboardShell role={user?.role} title={`Placement · ${campaign?.title}`} subtitle={`${creator?.name || "Creator"} × ${brand?.company_name || "Brand"}`}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 yc-card p-6">
                    <div className="yc-overline">Brief</div>
                    <p className="mt-2 leading-relaxed">{campaign?.brief}</p>
                    {campaign?.requirements && <div className="mt-3 text-sm bg-yc-cream/60 border-2 border-yc-ink rounded-xl p-3">{campaign.requirements}</div>}
                </div>
                <div className="yc-card-sm p-6">
                    <div className="yc-overline">Status</div>
                    <div className="font-heading font-black text-3xl mt-1 capitalize" data-testid="placement-status">{placement.status}</div>
                    <div className="yc-overline mt-4">Payment</div>
                    <div className="font-heading font-black text-2xl mt-1 capitalize" data-testid="placement-payment">{placement.payment_status}</div>
                    {isBrand && placement.status !== "complete" && (
                        <button data-testid="request-swap-btn" onClick={requestSwap} className="yc-btn-secondary w-full mt-4 text-sm py-2 px-3">Request swap</button>
                    )}
                </div>
            </div>

            <div className="yc-card p-6">
                <h3 className="font-heading text-xl font-bold">Deliverables ({placement.deliverables.length})</h3>
                <div className="mt-4 space-y-3">
                    {placement.deliverables.map((d, i) => (
                        <div key={`del-${d.title || i}-${i}`} className="border-2 border-yc-ink rounded-xl p-4 bg-white" data-testid={`deliverable-${i}`}>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="font-heading font-bold">{d.title}</div>
                                <span className="yc-badge bg-yc-cream">{d.status}</span>
                            </div>
                            {d.draft_url && <a href={d.draft_url} target="_blank" rel="noreferrer" className="text-sm text-yc-blue underline mt-2 inline-block">View draft</a>}
                            {d.notes && <div className="text-xs text-yc-ink/70 mt-1">{d.notes}</div>}
                            <div className="flex gap-2 mt-3 flex-wrap">
                                {isCreator && d.status !== "approved" && (
                                    <button data-testid={`submit-draft-${i}`} onClick={() => {
                                        const url = window.prompt("Draft URL (or upload link):");
                                        if (url) act(i, "submit-draft", { draft_url: url });
                                    }} className="yc-btn-secondary text-xs py-1 px-3">Submit draft</button>
                                )}
                                {isBrand && d.status === "draft-submitted" && (
                                    <>
                                        <button data-testid={`approve-${i}`} onClick={() => act(i, "approve")} className="yc-btn-primary text-xs py-1 px-3">Approve</button>
                                        <button data-testid={`revise-${i}`} onClick={() => {
                                            const notes = window.prompt("Revision notes:");
                                            if (notes) act(i, "request-revision", { notes });
                                        }} className="yc-btn-secondary text-xs py-1 px-3">Request revision</button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {isBrand && placement.status === "delivered" && (
                    <div className="mt-6 border-t-2 border-yc-ink pt-6">
                        {!showFinalize ? (
                            <button data-testid="open-finalize" onClick={() => setShowFinalize(true)} className="yc-btn-accent">Finalize, release payment & rate</button>
                        ) : (
                            <div className="space-y-3">
                                <div className="yc-overline">Brand rating (1-5)</div>
                                <input data-testid="brand-rating" type="number" min="1" max="5" className="yc-input" value={outcome.brand_rating} onChange={(e) => setOutcome({ ...outcome, brand_rating: parseFloat(e.target.value || 0) })} />
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="yc-overline">Reach</div>
                                        <input data-testid="outcome-reach" type="number" className="yc-input" value={outcome.outcome_data.reach} onChange={(e) => setOutcome({ ...outcome, outcome_data: { ...outcome.outcome_data, reach: parseInt(e.target.value || 0) } })} />
                                    </div>
                                    <div>
                                        <div className="yc-overline">Engagement</div>
                                        <input data-testid="outcome-engagement" type="number" className="yc-input" value={outcome.outcome_data.engagement} onChange={(e) => setOutcome({ ...outcome, outcome_data: { ...outcome.outcome_data, engagement: parseInt(e.target.value || 0) } })} />
                                    </div>
                                </div>
                                <div>
                                    <div className="yc-overline">Commercial usage rights</div>
                                    <input data-testid="usage-rights" className="yc-input" value={outcome.commercial_usage_rights} onChange={(e) => setOutcome({ ...outcome, commercial_usage_rights: e.target.value })} />
                                </div>
                                <textarea data-testid="rating-notes" rows={3} placeholder="Notes (optional)" className="yc-input" value={outcome.brand_rating_notes} onChange={(e) => setOutcome({ ...outcome, brand_rating_notes: e.target.value })} />
                                <button data-testid="finalize-btn" onClick={finalize} className="yc-btn-primary">Confirm & release payment</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Constructive feedback — the only brand -> creator channel */}
            <FeedbackCard placement={placement} isBrand={user?.role === "brand" || user?.role === "admin"}
                isCreatorView={user?.role === "creator"} placementId={placementId} reload={load} />

            {/* Asset Drop — shared workspace */}
            <AssetDrop placement={placement} reload={load} party={party()} />
        </DashboardShell>
    );

    function party() {
        if (user?.role === "admin") return "admin";
        if (user?.role === "brand") return "brand";
        return "creator";
    }
};

const FeedbackCard = ({ placement, isBrand, isCreatorView, placementId, reload }) => {
    const [fb, setFb] = useState({ went_well: "", ways_to_improve: "", what_could_change: "" });
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const workProduced = placement.status === "delivered" || placement.status === "complete"
        || (placement.assets || []).some((a) => a.uploaded_by === "creator")
        || (placement.deliverables || []).some((d) => ["draft-submitted", "approved"].includes(d.status));
    const existing = placement.feedback;

    const submit = async (e) => {
        e.preventDefault();
        setSending(true); setError("");
        try {
            await api.post(`/placement/${placementId}/feedback`, fb);
            await reload();
        } catch (err) {
            setError(err.response?.data?.detail || "Could not send feedback");
        } finally { setSending(false); }
    };

    return (
        <div className="yc-card p-6" data-testid="feedback-card">
            <h3 className="font-heading text-xl font-bold">Constructive feedback</h3>
            <p className="text-xs text-yc-ink/60 mt-1">
                {isBrand
                    ? "Once your creator has produced work, share structured feedback to boost their profile — what went well, ways to improve, and what could change."
                    : "Feedback from the brand appears here once they've reviewed your work — it helps boost your profile."}
            </p>
            {existing ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="feedback-display">
                    {[
                        { label: "What went well", key: "went_well", cls: "bg-yc-blue text-white" },
                        { label: "Ways to improve", key: "ways_to_improve", cls: "bg-yc-cream" },
                        { label: "What could change", key: "what_could_change", cls: "bg-white" },
                    ].map((s) => (
                        <div key={s.key} className={`border-2 border-yc-ink rounded-2xl p-4 ${s.cls}`}>
                            <div className={`yc-overline ${s.cls.includes("text-white") ? "!text-white" : ""}`}>{s.label}</div>
                            <p className="text-sm mt-2 leading-relaxed" data-testid={`feedback-${s.key}`}>{existing[s.key]}</p>
                        </div>
                    ))}
                </div>
            ) : isBrand ? (
                workProduced ? (
                    <form onSubmit={submit} className="mt-4 space-y-3" data-testid="feedback-form">
                        <div>
                            <div className="yc-overline">What went well</div>
                            <textarea data-testid="fb-went-well" required rows={2} className="yc-input mt-1" placeholder="The positives — what landed…" value={fb.went_well} onChange={(e) => setFb({ ...fb, went_well: e.target.value })} />
                        </div>
                        <div>
                            <div className="yc-overline">Ways to improve</div>
                            <textarea data-testid="fb-improve" required rows={2} className="yc-input mt-1" placeholder="Constructive pointers…" value={fb.ways_to_improve} onChange={(e) => setFb({ ...fb, ways_to_improve: e.target.value })} />
                        </div>
                        <div>
                            <div className="yc-overline">What could change</div>
                            <textarea data-testid="fb-change" required rows={2} className="yc-input mt-1" placeholder="Ideas for next time…" value={fb.what_could_change} onChange={(e) => setFb({ ...fb, what_could_change: e.target.value })} />
                        </div>
                        {error && <div className="text-yc-pink text-sm" data-testid="feedback-error">{error}</div>}
                        <button data-testid="feedback-submit" disabled={sending} className="yc-btn-primary text-sm py-2 px-4">{sending ? "Sending…" : "Send feedback"}</button>
                    </form>
                ) : (
                    <div className="mt-4 text-sm text-yc-ink/60 border-2 border-dashed border-yc-ink/30 rounded-2xl p-4" data-testid="feedback-locked">
                        Feedback unlocks once your creator has produced work for this campaign.
                    </div>
                )
            ) : (
                <div className="mt-4 text-sm text-yc-ink/60 border-2 border-dashed border-yc-ink/30 rounded-2xl p-4" data-testid="feedback-empty">
                    No feedback yet{isCreatorView ? " — keep an eye out once you've submitted work." : "."}
                </div>
            )}
        </div>
    );
};

const VIDEO_LIMIT = 10;
const PHOTO_LIMIT = 30;

const AssetDrop = ({ placement, reload, party }) => {
    const fileVideoRef = useRef(null);
    const filePhotoRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const assets = placement?.assets || [];
    const videos = assets.filter((a) => a.kind === "video");
    const photos = assets.filter((a) => a.kind === "photo");
    const canUpload = party === "creator" || party === "admin";

    const upload = async (file) => {
        if (!file) return;
        setUploading(true); setError("");
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/placement/${placement.placement_id}/asset/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await reload();
        } catch (e) {
            setError(e.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const remove = async (asset_id) => {
        if (!window.confirm("Remove this asset?")) return;
        await api.delete(`/placement/${placement.placement_id}/asset/${asset_id}`);
        await reload();
    };

    const download = async (a) => {
        // Hit the auth-gated download endpoint and force a save
        const res = await api.get(`/placement/${placement.placement_id}/asset/${a.asset_id}/download`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: a.content_type || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = a.original_filename;
        link.click();
        URL.revokeObjectURL(url);
    };

    const downloadAll = async () => {
        for (const a of assets) {
            // small stagger to avoid browser throttling
            await download(a);
            await new Promise((r) => setTimeout(r, 250));
        }
    };

    const Tile = ({ a }) => {
        const Icon = a.kind === "video" ? Film : ImageIcon;
        return (
            <div className="border-2 border-yc-ink rounded-2xl bg-white p-3 flex flex-col" data-testid={`asset-${a.asset_id}`}>
                <div className="aspect-square rounded-xl border-2 border-yc-ink bg-yc-cream flex items-center justify-center mb-3">
                    <Icon className="w-8 h-8 text-yc-blue" />
                </div>
                <div className="text-xs font-semibold truncate" title={a.original_filename}>{a.original_filename}</div>
                <div className="text-[10px] text-yc-ink/60 mt-1">{(a.size / 1024 / 1024).toFixed(2)} MB</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <button data-testid={`download-${a.asset_id}`} onClick={() => download(a)} className="yc-btn-secondary text-xs py-1.5 px-2" title="Download">
                        <Download className="w-3.5 h-3.5" />
                    </button>
                    {canUpload && (
                        <button data-testid={`remove-${a.asset_id}`} onClick={() => remove(a.asset_id)} className="yc-btn-secondary text-xs py-1.5 px-2" title="Remove">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="yc-card p-6" data-testid="asset-drop">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-yc-blue" />
                    <h3 className="font-heading text-xl font-bold">Asset drop</h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="yc-badge bg-yc-cream" data-testid="videos-count">{videos.length}/{VIDEO_LIMIT} videos</span>
                    <span className="yc-badge bg-yc-cream" data-testid="photos-count">{photos.length}/{PHOTO_LIMIT} photos</span>
                    {assets.length > 0 && (
                        <button data-testid="download-all" onClick={downloadAll} className="yc-btn-primary text-sm py-2 px-3">
                            <Download className="w-4 h-4 mr-2" /> Download all
                        </button>
                    )}
                </div>
            </div>

            <p className="text-sm text-yc-ink/70 mt-2">
                {canUpload
                    ? "Upload up to 10 videos and 30 photos. The brand can download them anytime."
                    : "Final deliverables from the creator. Download individually or grab all in one click."}
            </p>

            {canUpload && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <label className={`border-2 border-dashed border-yc-ink rounded-2xl p-6 text-center cursor-pointer hover:bg-yc-cream/40 transition-colors ${videos.length >= VIDEO_LIMIT ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-video-zone">
                        <Film className="w-7 h-7 mx-auto text-yc-blue" />
                        <div className="font-heading font-bold mt-2">Drop videos here</div>
                        <div className="text-xs text-yc-ink/60 mt-1">{videos.length}/{VIDEO_LIMIT} used</div>
                        <input ref={fileVideoRef} type="file" accept="video/*" hidden data-testid="upload-video-input" onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                    <label className={`border-2 border-dashed border-yc-ink rounded-2xl p-6 text-center cursor-pointer hover:bg-yc-cream/40 transition-colors ${photos.length >= PHOTO_LIMIT ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-photo-zone">
                        <ImageIcon className="w-7 h-7 mx-auto text-yc-pink" />
                        <div className="font-heading font-bold mt-2">Drop photos here</div>
                        <div className="text-xs text-yc-ink/60 mt-1">{photos.length}/{PHOTO_LIMIT} used</div>
                        <input ref={filePhotoRef} type="file" accept="image/*" hidden data-testid="upload-photo-input" onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                </div>
            )}

            {uploading && <div className="text-sm text-yc-blue font-semibold mt-3">Uploading…</div>}
            {error && <div className="text-sm text-yc-pink mt-3">{error}</div>}

            {videos.length > 0 && (
                <div className="mt-6">
                    <div className="yc-overline">Videos</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-3">
                        {videos.map((a) => <Tile key={a.asset_id} a={a} />)}
                    </div>
                </div>
            )}
            {photos.length > 0 && (
                <div className="mt-6">
                    <div className="yc-overline">Photos</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
                        {photos.map((a) => <Tile key={a.asset_id} a={a} />)}
                    </div>
                </div>
            )}
            {assets.length === 0 && !canUpload && (
                <div className="mt-6 text-center text-yc-ink/60 py-8 text-sm">Nothing uploaded yet — the creator will drop assets here when ready.</div>
            )}
        </div>
    );
};

export default Placement;
